// M135 (2026-05-10): Combobox cho field "Chủ đề" trong QuestionEditor.
// - Hiện danh sách topics đã có trong bộ (sort theo độ phổ biến — nhiều câu nhất hiện trước).
// - Cho phép tự gõ topic mới — option "+ Tạo mới" hiện ở cuối khi text không khớp topic nào.
// - API: GET /question-topics/:bo (đã có sẵn từ M3, không cần BE thay đổi).
// - Behavior: focus → mở dropdown; click outside → đóng; chọn item → fill + đóng.
//
// Dùng trong QuestionEditor.jsx thay cho <input type="text"> plain.
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

export default function TopicCombobox({
  value,
  onChange,
  bo,
  placeholder = 'Ví dụ: Vitamin bầu, Xe đẩy, ...',
}) {
  const [topics, setTopics] = useState([]); // [{topic, sort_order, cnt}]
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  // Fetch topics khi bo thay đổi
  useEffect(() => {
    if (!bo) {
      setTopics([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .topicsList(bo)
      .then((res) => {
        if (cancelled) return;
        // Lọc topic rỗng (BE COALESCE(topic, '') → có thể trả '') + sort theo cnt DESC
        const list = (res?.topics || [])
          .filter((t) => t.topic && t.topic.trim())
          .sort((a, b) => (b.cnt || 0) - (a.cnt || 0));
        setTopics(list);
      })
      .catch(() => !cancelled && setTopics([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [bo]);

  // Click outside → đóng dropdown
  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const trimmedValue = (value || '').trim();
  const lowerValue = trimmedValue.toLowerCase();

  // Filter theo input text (case-insensitive substring match)
  const filtered = trimmedValue
    ? topics.filter((t) => t.topic.toLowerCase().includes(lowerValue))
    : topics;

  // Có exact match (case-insensitive) trong list không
  const hasExactMatch = topics.some(
    (t) => t.topic.toLowerCase() === lowerValue,
  );

  // Hiện option "+ Tạo mới" khi user gõ text không trùng topic nào hiện có
  const showCreateOption = trimmedValue && !hasExactMatch;

  // Có gì để show không
  const hasContent = loading || filtered.length > 0 || showCreateOption || (open && topics.length === 0 && !loading);

  function handlePick(topic) {
    onChange(topic);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full border rounded px-3 py-2"
        autoComplete="off"
      />

      {open && hasContent && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg max-h-60 overflow-auto">
          {loading && (
            <div className="px-3 py-2 text-sm text-slate-500">Đang tải...</div>
          )}

          {!loading && filtered.length > 0 && (
            <ul>
              {filtered.map((t) => (
                <li key={t.topic}>
                  <button
                    type="button"
                    onClick={() => handlePick(t.topic)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex items-center justify-between"
                  >
                    <span className="truncate">{t.topic}</span>
                    <span className="text-xs text-slate-400 ml-2 shrink-0">
                      {t.cnt} câu
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!loading && filtered.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-sm text-slate-500">
              {topics.length === 0
                ? 'Bộ này chưa có chủ đề nào'
                : 'Không có chủ đề nào khớp'}
            </div>
          )}

          {showCreateOption && (
            <button
              type="button"
              onClick={() => handlePick(trimmedValue)}
              className={
                'w-full text-left px-3 py-2 text-sm bg-emerald-50 hover:bg-emerald-100 text-emerald-700 ' +
                (filtered.length > 0 ? 'border-t border-slate-200' : '')
              }
            >
              + Tạo chủ đề mới: <b>"{trimmedValue}"</b>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
